<template>
  <div
    class="fts-history"
    :data-history-load="loading ? 'loading' : 'loaded'"
    v-if="show"
  >
    <!-- history header -->
    <div class="history-header">
      <div class="history-header-container">
        <div
          class="history-header-wrapper"
          @click="toggleMode('comment')"
          :class="{
            'history-header-wrapper--active': mode === 'comment',
          }"
          v-if="commentSection"
        >
          Comment
        </div>
        <div
          class="history-header-wrapper"
          @click="toggleMode('history')"
          :class="{
            'history-header-wrapper--active': mode === 'history',
          }"
        >
          History
        </div>
        <div 
          class="history-header-wrapper" 
          :class="{
            'history-header-wrapper--active': mode === 'notification',
          }" 
          @click="toggleMode('notification')" 
          v-if="enableNotification"
        >
          Notification
        </div>
      </div>
    </div>
    <!-- history section -->
    <div
      class="history-section fts-scrollbar fts-scrollbar-overlay"
      ref="historySection"
    >
      <div key="load-archive" class="fts-text-center" v-if="historyArchive">
        <fts-btn
          text
          text-transform-init
          :loading="loading"
          class="history-load-btn fts-font-normal"
          @click="loadArchive"
        >
          Older history is archived, click to show
        </fts-btn>
      </div>

      <fts-spinner height="100%" v-if="initLoading" />

      <div class="history-section-intro" key="initial-notification" v-else-if="!locusId && mode == 'notification'">
        <font-awesome-icon :icon="['far', 'bell']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Notification status tracking
        </div>
        <div class="history-section-intro-description">
          Stay informed with notification status tracking for timely updates.
        </div>
      </div>
      
      <div class="history-section-intro" key="initial-history" v-else-if="!locusId && mode == 'history'">
        <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          See changes to this record
        </div>
        <div class="history-section-intro-description">
          Configuration changes
        </div>
      </div>

      <div class="history-section-intro" key="initial-comment" v-else-if="!locusId">
        <font-awesome-icon :icon="['far', 'message']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Start a conversation
        </div>
        <div class="history-section-intro-description">
          Let's create an entry to initiate collaboration with your team.
        </div>
      </div>
      
      <template v-else-if="mode === 'notification' && notification && notification.length">
        <fts-history-notification
          v-for="x in notification"
          :key="x.id"
          :users="users"
          :data="x"
        />
      </template>

      <div class="history-section-intro" key="empty-notification" v-else-if="mode == 'notification'">
        <font-awesome-icon :icon="['far', 'bell']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Notification status tracking
        </div>
        <div class="history-section-intro-description">
          Stay informed with notification status tracking for timely updates.
        </div>
      </div>

      <template v-else-if="(histories && histories.length)">
        <div key="load-more" class="fts-text-center" v-if="historyLoadMore">
          <fts-btn
            text
            text-transform-init
            :loading="loading"
            class="history-load-btn fts-font-normal"
            @click="nextPage"
          >
            more histories
          </fts-btn>
        </div>
        <div
          v-for="(history, historyIndex) in histories"
          :key="history.id"
          class="history-wrapper"
        >
          <fts-history-content
            :data.sync="histories[historyIndex]"
            :locus-id="locusId"
            :vector-id="vectorId"
            :states="states"
            :users="users"
            :comment-section="commentSection"
            :class="{ 'fts-event-pointers-none': scrolling }"
            :data-index="historyIndex"
            @comment:reply="replyComment"
          />
          <template
            v-if="
              commentSection &&
              history.historyItems &&
              history.historyItems.length
            "
          >
            <fts-history-content
              v-for="(item, itemIndex) in history.historyItems"
              :key="item.id"
              :data.sync="history.historyItems[itemIndex]"
              :vector-id="vectorId"
              :locus-id="locusId"
              :states="states"
              :users="users"
              child
              :comment-section="commentSection"
              :class="{ 'fts-event-pointers-none': scrolling }"
              @comment:reply="replyComment"
            />
          </template>
        </div>
      </template>

      <div class="history-section-intro" key="empty-history" v-else-if="mode == 'history'">
        <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          See changes to this record
        </div>
        <div class="history-section-intro-description">
          Configuration changes
        </div>
      </div>

      <div class="history-section-intro" key="empty-comment" v-else>
        <font-awesome-icon :icon="['far', 'message']" class="history-section-intro-icon" />
        <div class="history-section-intro-title">
          Start a conversation
        </div>
        <div class="history-section-intro-description">
          Share thoughts, react to comments, and collaboration with your team.
        </div>
      </div>
    </div>
    <!-- history footer -->
    <div class="history-footer" v-if="commentSection && locusId && mode !== 'notification'">
      <fts-comment
        :locus-id="locusId"
        :vector-id="vectorId"
        :avatar="false"
        :send-action="true"
        v-model="collaboration"
        @added="addHistory($event)"
        @submitting="updateHistorySubmitQueue"
        ref="historySectionComment"
      />
    </div>
  </div>
</template>

<script>
import { classificationStore } from '../../store/classificationStore';

import FTSBtn from '../FTSBtn';
import FTSComment from '../FTSComment';
import FTSSpinner from '../FTSSpinner';
import FTSHistoryContent from './FTSHistoryContent';
import FTSHistoryNotification from './FTSHistoryNotification';

let timer = null;
const scroll = (vm) => {
  clearTimeout(timer);
  timer = null;
  if (!vm.scrolling) {
    vm.scrolling = true;
  }
  timer = setTimeout(() => {
    vm.scrolling = false;
  }, 600);
};

const MESSAGE = {
  historySubmitQueue: "Please wait for the comment to be submitted.",
};

export default {
  props: {
    locusId: [String, Number],
    vectorId: [String, Number],
    states: Array,
    users: Array,
    commentSection: {
      type: Boolean,
      default: true,
    },
    enableNotification: {
      type: Boolean,
      default: true
    },
    initOpen: Boolean
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-comment": FTSComment,
    "fts-spinner": FTSSpinner,
    "fts-history-content": FTSHistoryContent,
    "fts-history-notification": FTSHistoryNotification,
  },
  data: () => ({
    show: false,
    initLoading: false,
    loading: false,
    scrolling: false,
    mode: "comment",
    histories: [],
    historyCurrentPage: 0,
    historyLoadMore: false,
    historyArchive: false,
    notification: [],
    collaboration: { attachments: [] },
    historySubmitQueue: []
  }),
  methods: {
    resetScroll() {
      this.$refs.historySection && this.$refs.historySection.scrollTo(0, 0);
    },
    replyComment(content) {
      if (this.historySubmitQueue.length) {
        this.$toasted.info(MESSAGE.historySubmitQueue);
      } else {
        Boolean(content) && this.$set(this.collaboration, "content", content);
        this.focusSectionComment();
      }
    },
    updateHistorySubmitQueue(item) {
      if (item.isSubmitting) {
        this.historySubmitQueue.push(item.id);
      } else {
        const idx = this.historySubmitQueue.indexOf(item.id);
        idx > -1 && this.historySubmitQueue.splice(idx, 1);
      }
    },
    addHistory(item) {
      this.histories.push(item);
      this.$emit("added");
      this.$refs.historySection && this.$nextTick(() => {
        this.$refs.historySection.scrollTop = this.$refs.historySection.scrollHeight;
      });
    },
    async retrieveHistory(loadMore, withArchive) {
      if (this.locusId && this.show) {
        const historyMode = this.mode;
        this.loading = true;
        if (loadMore) {
          this.historyCurrentPage += 1;
        } else {
          this.resetScroll();
          this.historyCurrentPage = 0;
        }

        try {
          const res = await classificationStore.dispatch("retrieveHistory", {
            moduleId: this.vectorId,
            id: this.locusId,
            history: historyMode == "history",
            currentPage: this.historyCurrentPage,
            archive: withArchive
          });

          if (historyMode != this.mode) return;

          if (loadMore) {
            const firstEl = document.querySelector('[data-index="0"]')
            const firstElOffsetTop = firstEl.offsetTop;
            this.histories = _.sortBy([
              ...res.data.histories.filter((x) => !this.histories.some((y) => y.id === x.id)),
              ...this.histories
            ], 'on');

            this.$nextTick(() => {
              this.$refs.historySection.scrollTo(0, firstEl.offsetTop - firstElOffsetTop)
            });
          } else {
            this.histories = res.data.histories;
          }

          this.historyLoadMore = Boolean(res.data.hasMore);
          this.historyArchive = Boolean(res.data.hasArchive);
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    async retrieveNotification() {
      if (this.locusId && this.show) {
        this.loading = true;
        this.resetScroll();

        try {
          let res = await classificationStore.dispatch("retrieveNotification", {
            moduleId: this.vectorId,
            id: this.locusId,
          });
          this.notification = res.data.notifications;
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    nextPage() {
      if (!this.loading) {
        this.retrieveHistory(true);
      }
    },
    loadArchive() {
      if (!this.loading) {
        this.retrieveHistory(false, true);
      }
    },
    async toggleHistory() {
      this.show = !this.show;
      if (this.show) {
        this.initLoading = true;
        await this.retrieveHistory();
        this.initLoading = false;
      }
    },
    async toggleMode(mode) {
      if (this.historySubmitQueue.length) {
        this.$toasted.info(MESSAGE.historySubmitQueue);
      } else if (this.mode !== mode) {
        this.initLoading = true;
        this.mode = mode;
        this.collaboration = { attachments: [] };
        if (this.mode === "notification") {
          await this.retrieveNotification();
        } else {
          await this.retrieveHistory();
        }
        this.initLoading = false;
      }
    },
    focusSectionComment() {
      if (!this.$refs.historySectionComment.showComment) {
        this.$refs.historySectionComment.showComment = true;
      }
      this.$refs.historySectionComment.onFocus();
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.historySection.addEventListener("scroll", () => scroll(this), false);
        });
      } else if (this.$refs.historySection) {
        this.$refs.historySection.removeEventListener("scroll", scroll, false);
      }
    }
  },
  mounted() {
    if (!this.commentSection) {
      this.mode = "history"
    }
    if (this.initOpen) {
      this.toggleHistory();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.fts-history {
  $width: 400px !default;
  flex: 1 1 $width;
  max-width: $width;
  min-width: $width;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--fts-border);
  background-color: var(--fts-background);
  .history-header {
    padding: 0.5rem 0.75rem;
    .history-header-container {
      display: flex;
      align-items: center;
      .history-header-wrapper {
        padding-bottom: 0.125rem;
        border-bottom: 2px solid transparent;
        font-weight: 500;
        font-size: var(--inistate-label-text-14);
        line-height: var(--inistate-paragraph-text-14--line-height);
        color: rgba(var(--fts-default-rgb), 0.62);
        transition: all 0.3s ease-in-out;
        cursor: pointer;
        &:only-child {
          cursor: default;
        }
        &:not(:last-child) {
          margin-right: 1rem;
        }
        &:hover {
          color: rgba(var(--fts-default-rgb), 0.85);
        }
        &.history-header-wrapper--active {
          border-color: var(--fts-default);
          color: var(--fts-default);
        }
      }
    }
  }
  .history-section {
    flex: 1;
    position: relative;
    padding-bottom: 0.5rem;
  }
  .history-section-intro {
    height: 100%;
    @include flex-center-middle;
    flex-direction: column;
    padding: 3rem 2rem;
    text-align: center;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
  }
  .history-section-intro-icon {
    margin-bottom: 0.75rem;
    font-size: 1.5rem;
    color: var(--fts-default);
  }
  .history-section-intro-title {
    margin-bottom: 0.25rem;
    color: var(--fts-text);
  }
  .history-section-intro-description {
    color: var(--fts-default);
  }
  .history-footer {
    padding: 0.5rem;
    border-top: 1px solid var(--fts-border);
  }
  .history-load-btn {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
}
</style>